import _get from 'lodash/get';
import { LAYOUT_ENTITIES } from '@bedrock/fido-sdk';
import {
  AD_JINGLE_DURATION,
  PLAYBACK_STATE_ENDED,
  PLAYBACK_STATE_PAUSED,
  PLAYBACK_STATE_PLAYING,
  PLAYER_MODE,
} from './player.constants';
import {
  getLiveSeekFeature,
  getPlayerJingleFeature,
  getPlayerPauseAdFeature,
  getPlayerLBannerFeature,
  getPlayerPauseAdVastFeature,
} from '../features/features.selectors';

const DEFAULT_OBJECT = {};

// Playback state

export const getIsAdProviderReady = ({ player }) => player.isAdProviderReady;

export const getIsAdError = ({ player }) => player.isAdError;

export const getCanPlay = ({ player }) => player.canPlay;

export const getIsLoadingContent = ({ player }) => player.isLoadingContent;

export const getPlaybackState = ({ player }) => player.playbackState;

export const getAdPlaybackState = ({ player }) => player.adPlaybackState;

export const getIsStarted = ({ player }) => player.isStarted;

export const getIsEnded = ({ player }) => player.playbackState === PLAYBACK_STATE_ENDED;

export const getIsBuffering = ({ player }) => player.isBuffering;

// Player mode (the single, mutually-exclusive on-screen surface)

// Surfaces owned by the ad-engine
const AD_MODES = [
  PLAYER_MODE.BEFORE_AD_JINGLE_IMAGE,
  PLAYER_MODE.BEFORE_AD_JINGLE_CLIP,
  PLAYER_MODE.AD,
  PLAYER_MODE.AFTER_AD_JINGLE_CLIP,
];

// App-owned jingle/loader surfaces shown over the (re)loading content.
const JINGLE_MODES = [PLAYER_MODE.VIDEO_JINGLE, PLAYER_MODE.PROGRAM_JINGLE, PLAYER_MODE.AFTER_AD_JINGLE_IMAGE];

export const getPlayerMode = ({ player }) => player.mode;

export const isAdPlayerMode = mode => AD_MODES.includes(mode);

export const isAdMode = state => isAdPlayerMode(getPlayerMode(state));

export const isJingleMode = state => JINGLE_MODES.includes(getPlayerMode(state));

export const isContentMode = state => getPlayerMode(state) === PLAYER_MODE.CONTENT;

// True only while a real advertisement is on screen (not a jingle clip/image, not the
// break-start gap). This is the single gate for ad-only UI such as the countdown pill:
// the other ad surfaces share the ads player/focus (isAdMode) but must not show it.
export const isAdvertisementMode = state => getPlayerMode(state) === PLAYER_MODE.AD;

/**
 * True while there is nothing to watch yet: the content is loading behind a jingle surface (shown or not), a break is
 * starting or loading its next ad, or the content is buffering. Drives the loading spinner on every transition.
 */
export const isPlayerLoading = state => {
  const mode = getPlayerMode(state);

  if (mode === PLAYER_MODE.CONTENT) {
    return Boolean(getIsBuffering(state));
  }

  if (JINGLE_MODES.includes(mode) || mode === PLAYER_MODE.BEFORE_AD_JINGLE_IMAGE) {
    return true;
  }

  const adPlaybackState = getAdPlaybackState(state);

  return adPlaybackState !== PLAYBACK_STATE_PLAYING && adPlaybackState !== PLAYBACK_STATE_PAUSED;
};

export const getIsSeeking = ({ player }) => player.isSeeking;

export const getAudioTracks = ({ player }) => player.audioTracks;

export const getSubtitleTracks = ({ player }) => player.subtitleTracks;

export const getSelectedAudioTrack = ({ player }) => player.selectedAudioTrack;

export const getSelectedSubtitlesTrack = ({ player }) => player.selectedSubtitlesTrack;

export const getSubtitleCuePoint = ({ player }) => player.subtitleCuePoint;

export const getLastPlaybackError = ({ player }) => player.lastError;

export const getContentType = ({ player }) => player.contentType;

// True while a video (not an audio) content or its ads are on screen, which keeps the screen saver away
export const isVideoPlaybackActive = state =>
  ![LAYOUT_ENTITIES.audio, LAYOUT_ENTITIES.radio].includes(getContentType(state)) &&
  (isAdMode(state) || getPlaybackState(state) === PLAYBACK_STATE_PLAYING);

// Metadata

export const getCurrentMetadataItem = state =>
  _get(state, 'player.metadataItems', [])[_get(state, 'player.currentMetadataItemIndex')];

export const getMetadataItems = state => state.player.metadataItems;

export const getTitle = state => _get(getCurrentMetadataItem(state), 'title', '');

export const getExtraTitle = state => _get(getCurrentMetadataItem(state), 'extraTitle', '');

export const getCSAPicto = state => _get(getCurrentMetadataItem(state), 'csaPicto');

export const getHasAgeGate = state => _get(getCSAPicto(state), 'name') === 'Csa18';

// Playback timings

export const getDurationInSeconds = state => _get(state, 'player.durationInSeconds', 0);

export const getElapsedTimeInSeconds = state => _get(state, 'player.elapsedTimeInSeconds');

export const getStartTimeInSeconds = state => _get(state, 'player.startTimeInSeconds', 0);

export const getLastViewedClipId = state => _get(state, 'player.lastViewedClipId', '');

export const getLastViewedClipProgress = state => _get(state, 'player.lastViewedClipProgress', 0);

// Layout data

// NOTE: ignore missing feature switch key
export const isJingleEnabled = state => getPlayerJingleFeature(state) !== false;

export const getJingleImageId = state => (isJingleEnabled(state) && state.player.jingleImageId) || '';

export const getProgramImageId = state => (isJingleEnabled(state) && state.player.programImageId) || '';

export const getPlayerLocked = state => state.player.isLocked;

// NOTE: the block is captured on the player initialization and paginated within the player state,
// apart from the layout cache, so the panel could load more items without disturbing the playback
export const getSideGlassContent = state => state.player.sideGlass;

export const getContentAdvisory = state => _get(getCurrentMetadataItem(state), 'contentAdvisory', DEFAULT_OBJECT);

export const getAnalyticsById = analyticsId => state =>
  _get(getCurrentMetadataItem(state), `analytics.${analyticsId}`, DEFAULT_OBJECT);

// Ads

export const getAdvertising = state => _get(getCurrentMetadataItem(state), 'advertising', DEFAULT_OBJECT);

export const getVideoAds = state => state.player.videoAds;

export const getAdsProvider = state => {
  const ads = getAdvertising(state);

  return ads.providers ? ads.providers.video : '';
};

export const getPauseAdRequestData = state => {
  const isGAMEnabled = getPlayerPauseAdFeature(state);
  const isVASTEnabled = getPlayerPauseAdVastFeature(state);

  const advertising = _get(getCurrentMetadataItem(state), 'advertising');
  if (!advertising) {
    return undefined;
  }

  let provider = '';
  // NOTE: VAST and GAM features are exclusive, with VAST taking precedence
  // See https://bedrockstreaming.atlassian.net/browse/STB-556 for field names
  if (isVASTEnabled) {
    provider = _get(advertising, 'providers.pausevast');
  } else if (isGAMEnabled) {
    provider = _get(advertising, 'providers.pause');
  }

  if (!provider) {
    return undefined;
  }

  return advertising[provider];
};

export const getPauseAdState = state => state.player.pauseAdState;

export const getLBannerState = state => state.player.lBannerState && getPlayerLBannerFeature(state);

export const getCurrentPauseAd = state => state.player.pauseAd;

export const getDisplayedAdBreakType = state => _get(state, 'player.displayedAdBreakType');

export const getAdBreakDuration = state => state.player.adBreakDuration;

export const getAdElapsedTime = state => state.player.adElapsedTime;

export const getAdSkipOffset = state => state.player.adSkipOffset;

export const getAdBreakElapsedTime = state => state.player.adBreakElapsedTime;

export const getAdJingleDurationInMilliseconds = state =>
  _get(state, 'app.ads.adJingleDurationInMilliseconds', AD_JINGLE_DURATION);

export const getAdJingleImageId = state => _get(state, 'player.adJingleImageId', '');

// The jingle image to render for the current mode (empty string = no jingle surface).
export const getJingleSurfaceImageId = state => {
  switch (getPlayerMode(state)) {
    case PLAYER_MODE.VIDEO_JINGLE:
    case PLAYER_MODE.AFTER_AD_JINGLE_IMAGE:
      return getJingleImageId(state);
    case PLAYER_MODE.PROGRAM_JINGLE:
      return getProgramImageId(state);
    case PLAYER_MODE.BEFORE_AD_JINGLE_IMAGE:
      return getAdJingleImageId(state);
    default:
      return '';
  }
};

export const isAdJingleDisplayed = state => getPlayerMode(state) === PLAYER_MODE.BEFORE_AD_JINGLE_IMAGE;

export const getAdMarkers = state => state.player.adMarkers;

// Video source data

export const getVideoId = state => _get(getCurrentMetadataItem(state), 'video.id', '');

export const getClipId = state => getVideoId(state).replace('clip_', '');

export const getSources = state => _get(state, 'player.sources', []);

export const getCurrentSourceIndex = state => _get(state, 'player.currentSourceIndex');

export const getVideoSource = state => {
  const sourceIndex = _get(state, 'player.currentSourceIndex', 0);

  return _get(state, `player.sources[${sourceIndex}]`);
};

export const getCurrentAssetType = state => {
  const source = getVideoSource(state);

  if (!source) {
    return '';
  }

  const { provider, format, container } = source;

  return [provider, format, container].join('_');
};

export const getIsLive = state => _get(state, 'player.isLive', false);

export const shouldSwitchToNextProgram = state => getElapsedTimeInSeconds(state) >= getDurationInSeconds(state);

export const shouldLoadNewMetadataItems = state => {
  const {
    currentMetadataItemIndex,
    elapsedTimeInSeconds,
    lastMetadataItemIndex,
    timeToLoadNewLayout,
    isNextLiveLayoutLoading,
  } = state.player;

  return Boolean(
    !isNextLiveLayoutLoading &&
      Number.isFinite(timeToLoadNewLayout) &&
      currentMetadataItemIndex === lastMetadataItemIndex &&
      elapsedTimeInSeconds >= timeToLoadNewLayout,
  );
};

// Features

export const isLiveStartOverEnabled = state => Boolean(state.player.liveStartOverFeature);

export const isLiveSeekEnabled = state => Boolean(isLiveStartOverEnabled(state) && getLiveSeekFeature(state));

// L-Banner

export const getLBannerProvider = state => {
  const ads = getAdvertising(state);

  return ads.providers ? ads.providers.nonlinear : '';
};
